/**
 * Choosing a language, and rendering a string in it.
 *
 * Hand-rolled rather than i18next, and that is a deliberate size judgement
 * rather than a preference. This workspace has four dependencies; the two
 * languages it needs both take simple one/other plurals and neither is
 * right-to-left. What a library would buy here is mostly machinery for
 * problems this product does not have, in exchange for a bundle every parent
 * on a phone downloads.
 *
 * The rule the whole module is built around: EVERY path ends in readable
 * text. A wrong language is visible and fixable from the dropdown; a blank
 * space or the word "undefined" is neither, and reads as a broken page.
 */

export const LOCALES = ['en', 'ne'] as const;
export type Locale = (typeof LOCALES)[number];

/** The language names as their own speakers write them, for the dropdown. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ne: 'नेपाली',
};

/**
 * Is this one of the languages we ship?
 *
 * EXPORTED, because the alternative was a cast. `LanguagePicker` read
 * `e.target.value` — a plain string — and wrote `setLocale(value as Locale)`,
 * which is the point where the type system stops checking the one value in
 * this app that indexes a dictionary. A locale that is not 'en' or 'ne' makes
 * the active table undefined, and before the guard below that threw on every
 * single string: not a mistranslated word, the whole application replaced by
 * the error boundary, on every screen. Callers narrow with this instead.
 */
export const isLocale = (v: unknown): v is Locale =>
  typeof v === 'string' && (LOCALES as readonly string[]).includes(v);

/**
 * Which language to show.
 *
 * An explicit choice always wins — somebody who picked English on a Nepali
 * phone meant it. Failing that the browser is asked, and failing that
 * English, because the product was written in it and a missing translation
 * degrades to it anyway.
 */
export function resolveLocale(
  { stored, preferred }: { stored: string | null | undefined; preferred: readonly string[] },
): Locale {
  if (isLocale(stored)) return stored;

  for (const tag of preferred) {
    // Browsers send 'ne-NP', not 'ne'. Comparing whole tags would match
    // nothing and hand every Nepali speaker English without a word.
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }

  return 'en';
}

export type Dict = Record<string, string>;

/**
 * A `t` bound to one language, falling back through English.
 *
 * Three layers on purpose, because the steady state of a translated product
 * is that copy lands in English and is translated later:
 *
 *   the active language → English → the key itself
 *
 * The last is not a formality. Returning '' for an unknown key produces a
 * blank space that looks like the design and gets reported by nobody; the key
 * is ugly, obvious in a screenshot, and greppable.
 *
 * `active` IS OPTIONAL, which is a fourth layer and an admission. The caller
 * looks it up as `DICTS[locale]`, and that is undefined for a locale which is
 * not one of ours — so the table being absent is a real state, not a
 * hypothetical. Reading `active[key]` off it threw on EVERY string rather
 * than on one, which took the error boundary and the whole app with it. The
 * module's rule is that every path ends in readable text; this is that rule
 * applied to its own argument.
 */
export function translator<D extends Dict>(english: D, active?: Dict) {
  return (key: keyof D & string, vars?: Record<string, string | number>): string => {
    const template = active?.[key] ?? english[key] ?? key;
    if (!vars) return template;

    /*
     * Named, not positional. Nepali reorders — "{max} मध्ये {score}" against
     * English's "{score} out of {max}" — and a positional scheme silently
     * swaps a child's score with the paper's total.
     *
     * A placeholder with no value is LEFT AS IT IS rather than replaced with
     * "undefined": the former looks like missing data, which it is, and the
     * latter looks like a crash.
     */
    return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
      const value = vars[name];
      return value === undefined || value === null ? whole : String(value);
    });
  };
}

/* ------------------------------------------------------------ persistence */

export const LOCALE_KEY = 'ca.locale';

/** The two methods this needs, and nothing else. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The stored choice, or null when there is not a usable one.
 *
 * Null rather than a default, because "never chosen" and "chose English" are
 * different: the first should let the browser's language win, the second
 * should not. Guarded because `localStorage` THROWS rather than returning
 * null when a browser has blocked site data.
 */
export function readStoredLocale(store: KeyValueStore | null | undefined): Locale | null {
  try {
    const raw = store?.getItem(LOCALE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredLocale(
  store: KeyValueStore | null | undefined, locale: Locale,
): void {
  try {
    store?.setItem(LOCALE_KEY, locale);
  } catch {
    // The choice still applies for this session; it is simply not remembered.
  }
}
